import { ReactNode, useEffect, useState, lazy, Suspense } from "react";
import { CinematicCtx } from "./useCinematicScroll";

const DepthCanvas = lazy(() =>
  import("./DepthCanvas").then((m) => ({ default: m.DepthCanvas })),
);

interface CinematicStageProps {
  children: ReactNode;
}

/**
 * Wraps the page. Renders a fixed-position cinematic backdrop
 * (three.js depth scene + aurora gradient) behind the existing content,
 * and exposes 0..1 scroll progress to the rest of the cinematic system.
 *
 * - Disabled (aurora-only) on reduced-motion or viewport < 640px
 * - Backdrop is pointer-events:none — never blocks clicks
 */
export function CinematicStage({ children }: CinematicStageProps) {
  const [progress, setProgress] = useState(0);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const reducedQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const mobileQuery = window.matchMedia("(max-width: 639px)");
    const update = () => setReduced(reducedQuery.matches || mobileQuery.matches);
    update();
    reducedQuery.addEventListener("change", update);
    mobileQuery.addEventListener("change", update);
    return () => {
      reducedQuery.removeEventListener("change", update);
      mobileQuery.removeEventListener("change", update);
    };
  }, []);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const doc = document.documentElement;
        const max = doc.scrollHeight - window.innerHeight;
        const p = max > 0 ? Math.min(1, Math.max(0, window.scrollY / max)) : 0;
        setProgress(p);
        raf = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // Aurora hue rotates with progress (220° → 180°, blue → cyan)
  const hue = 220 - progress * 40;
  const bloom = Math.sin(progress * Math.PI);

  return (
    <CinematicCtx.Provider value={{ progress, reduced }}>
      {/* Fixed cinematic backdrop */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-0 overflow-hidden"
        style={{ contain: "strict" }}
      >
        {/* Aurora gradient layer — always on */}
        <div
          className="absolute inset-0 transition-[background] duration-700"
          style={{
            background: `
              radial-gradient(60% 50% at 30% 20%, hsla(${hue}, 90%, 60%, ${0.18 + bloom * 0.08}) 0%, transparent 60%),
              radial-gradient(50% 40% at 80% 70%, hsla(${hue - 30}, 90%, 55%, ${0.14 + bloom * 0.06}) 0%, transparent 60%),
              radial-gradient(80% 60% at 50% 110%, hsla(${hue + 20}, 80%, 50%, ${0.10 + bloom * 0.05}) 0%, transparent 70%)
            `,
            filter: "blur(4px)",
          }}
        />
        {/* Three.js depth scene — only when not reduced */}
        {!reduced && (
          <Suspense fallback={null}>
            <DepthCanvas />
          </Suspense>
        )}
        {/* Subtle film grain / vignette */}
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 50%, hsl(var(--background) / 0.7) 100%)",
          }}
        />
      </div>

      {/* Content sits above */}
      <div className="relative z-10">{children}</div>
    </CinematicCtx.Provider>
  );
}
